import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, RefreshCw, Send, Trash2, Webhook } from "lucide-react";
import { toast } from "../lib/toast";
import type { WebhookEvent } from "@smsbridge/shared";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Breadcrumb } from "../components/ui/Breadcrumb";
import { Collapse } from "../components/ui/Collapse";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/ui/PageHeader";
import { WebhookCardsSkeleton } from "../components/ui/Skeletons";
import { RowAction } from "../components/ui/RowAction";
import { StatusPill } from "../components/ui/StatusPill";
import { api, apiErrorMessage } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useMinimumDuration } from "../lib/useMinimumDuration";
import { cn } from "../lib/cn";

interface WebhookRow {
  _id: string;
  url: string;
  events: string[];
  isActive: boolean;
  customHeaders: Record<string, string>;
  createdAt: string;
}

interface DeliveryRow {
  _id: string;
  attempt: number;
  statusCode: number | null;
  latencyMs: number | null;
  responseBody: string | null;
  nextRetryAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

const ALL_EVENTS: Array<{ value: WebhookEvent; help: string }> = [
  { value: "message.received", help: "An inbound SMS arrived on a paired phone." },
  { value: "message.status.changed", help: "A sent message moved to SENT, DELIVERED or FAILED." },
  { value: "device.online", help: "A phone started checking in again." },
  { value: "device.offline", help: "A phone stopped checking in." },
  { value: "quota.updated", help: "Your daily or monthly usage changed." },
  { value: "batch.progress", help: "A bulk send made progress." },
];

function formatWhen(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const VERIFY_SNIPPET = `// Node.js — verify the signature before trusting a payload
import { createHmac, timingSafeEqual } from "crypto";

app.post("/webhooks/smsbridge", express.raw({ type: "*/*" }), (req, res) => {
  const signature = req.header("X-SmsBridge-Signature");
  const timestamp = req.header("X-SmsBridge-Timestamp");
  const expected = createHmac("sha256", process.env.SMSBRIDGE_SECRET)
    .update(\`\${timestamp}.\${req.body.toString("utf8")}\`)
    .digest("hex");

  const a = Buffer.from(expected);
  const b = Buffer.from(signature ?? "");
  if (a.length !== b.length || !timingSafeEqual(a, b)) return res.sendStatus(401);

  res.sendStatus(200);
});`;

export function WebhooksPage() {
  const queryClient = useQueryClient();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookRow | null>(null);
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>(["message.received"]);
  const [headersText, setHeadersText] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [secretFor, setSecretFor] = useState<WebhookRow | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebhookRow | null>(null);

  const webhooksQuery = useQuery({
    queryKey: ["webhooks"],
    queryFn: async () => (await api.get<WebhookRow[]>("/webhooks")).data,
  });

  const webhooksLoading = useMinimumDuration(webhooksQuery.isLoading);

  const deliveriesQuery = useQuery({
    queryKey: ["webhook-deliveries", expanded],
    queryFn: async () =>
      (await api.get<DeliveryRow[]>(`/webhooks/${expanded}/deliveries`, { params: { limit: 25 } }))
        .data,
    enabled: expanded !== null,
  });

  function openCreate() {
    setEditing(null);
    setUrl("");
    setEvents(["message.received"]);
    setHeadersText("");
    setEditorOpen(true);
  }

  function openEdit(row: WebhookRow) {
    setEditing(row);
    setUrl(row.url);
    setEvents(row.events as WebhookEvent[]);
    setHeadersText(
      Object.entries(row.customHeaders ?? {})
        .map(([k, v]) => `${k}: ${v}`)
        .join("\n"),
    );
    setEditorOpen(true);
  }

  /** "Name: value" per line — friendlier to type than JSON, and trivially unambiguous. */
  function parseHeaders(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const line of headersText.split("\n")) {
      const idx = line.indexOf(":");
      if (idx <= 0) continue;
      const key = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (key) out[key] = value;
    }
    return out;
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = { url, events, customHeaders: parseHeaders() };
      if (editing) return (await api.patch(`/webhooks/${editing._id}`, payload)).data;
      return (await api.post("/webhooks", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Webhook updated." : "Webhook created.");
      setEditorOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not save the webhook.")),
  });

  const toggleMutation = useMutation({
    mutationFn: async (row: WebhookRow) =>
      api.patch(`/webhooks/${row._id}`, { isActive: !row.isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["webhooks"] }),
    onError: (err) => toast.error(apiErrorMessage(err, "Could not update the webhook.")),
  });

  const testMutation = useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ ok: boolean; statusCode: number | null; latencyMs: number; error: string | null }>(
          `/webhooks/${id}/test`,
        )
      ).data,
    onSuccess: (result, id) => {
      if (result.ok) toast.success(`Endpoint answered ${result.statusCode} in ${result.latencyMs}ms.`);
      else toast.error(result.error ?? `Endpoint answered ${result.statusCode ?? "nothing"}.`);
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries", id] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Test failed.")),
  });

  const replayMutation = useMutation({
    mutationFn: async (deliveryId: string) =>
      api.post(`/webhooks/deliveries/${deliveryId}/replay`),
    onSuccess: () => {
      toast.success("Queued for another attempt.");
      void queryClient.invalidateQueries({ queryKey: ["webhook-deliveries"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not replay.")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      toast.success("Webhook deleted.");
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["webhooks"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not delete.")),
  });

  async function showSecret(row: WebhookRow) {
    setSecretFor(row);
    setSecret(null);
    const { data } = await api.get<{ secret: string }>(`/webhooks/${row._id}/secret`);
    setSecret(data.secret);
  }

  const rotateMutation = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ secret: string }>(`/webhooks/${id}/rotate-secret`)).data,
    onSuccess: (result) => {
      setSecret(result.secret);
      toast.success("Secret rotated. Update your receiver before the next event.");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not rotate the secret.")),
  });

  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title="Webhooks"
        description="Get an HTTP callback when an SMS arrives or a delivery status changes. Every request is signed so you can verify it came from us."
        actions={
          <Button leftIcon={<Webhook className="size-4" aria-hidden />} onClick={openCreate}>
            Add endpoint
          </Button>
        }
      />

      {webhooksQuery.isError && (
        <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {apiErrorMessage(webhooksQuery.error, "Could not load your webhooks.")}
        </p>
      )}

      {webhooksLoading && <WebhookCardsSkeleton />}

      {!webhooksQuery.isLoading && (webhooksQuery.data ?? []).length === 0 && (
        <EmptyState
          icon={<Webhook className="size-6" aria-hidden />}
          title="No endpoints yet"
          description="Add a URL and we'll POST an event to it whenever something happens on your gateway."
          action={<Button onClick={openCreate}>Add endpoint</Button>}
        />
      )}

      <div className="flex flex-col gap-3">
        {(webhooksQuery.data ?? []).map((row) => (
          <Card key={row._id} padding="lg" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <code className="break-all text-sm font-medium text-text">{row.url}</code>
                  {row.isActive ? (
                    <StatusPill status="ACTIVE" />
                  ) : (
                    <StatusPill status="DISABLED" />
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {row.events.map((event) => (
                    <Badge key={event} tone="neutral" className="font-mono text-[11px]">
                      {event}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Send className="size-4" aria-hidden />}
                  loading={testMutation.isPending && testMutation.variables === row._id}
                  onClick={() => testMutation.mutate(row._id)}
                >
                  Send test
                </Button>
                <Button variant="secondary" size="sm" onClick={() => void showSecret(row)}>
                  Secret
                </Button>
                <Button variant="secondary" size="sm" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  loading={toggleMutation.isPending && toggleMutation.variables?._id === row._id}
                  onClick={() => toggleMutation.mutate(row)}
                >
                  {row.isActive ? "Disable" : "Enable"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger hover:bg-danger-soft"
                  leftIcon={<Trash2 className="size-4" aria-hidden />}
                  onClick={() => setDeleteTarget(row)}
                >
                  Delete
                </Button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setExpanded(expanded === row._id ? null : row._id)}
              aria-expanded={expanded === row._id}
              className={cn(
                "flex items-center gap-1.5 self-start rounded-control px-2 py-1 -mx-2 text-sm font-medium text-text-muted",
                "transition-colors duration-fast ease-standard hover:bg-surface-hover hover:text-text",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
              )}
            >
              <ChevronDown
                className={cn(
                  "size-4 transition-transform duration-base ease-standard motion-reduce:transition-none",
                  expanded === row._id && "rotate-180",
                )}
                aria-hidden
              />
              {expanded === row._id ? "Hide" : "Show"} delivery log
            </button>

            {/*
              Kept mounted and collapsed rather than unmounted, so the panel has a height to
              animate to. The delivery query is already gated on `expanded`, so a collapsed
              card still fetches nothing.
            */}
            <Collapse open={expanded === row._id}>
              <div className="mb-2">
                <Breadcrumb
                  items={[
                    { label: "Webhooks", onClick: () => setExpanded(null) },
                    { label: row.url },
                    { label: "Delivery log" },
                  ]}
                />
              </div>
              <div className="scrollbar-slim overflow-x-auto rounded-card border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-3 py-2 font-medium text-text-muted">When</th>
                      <th className="px-3 py-2 font-medium text-text-muted">Status</th>
                      <th className="px-3 py-2 font-medium text-text-muted">Attempt</th>
                      <th className="px-3 py-2 font-medium text-text-muted">Latency</th>
                      <th className="px-3 py-2 font-medium text-text-muted">Response</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {deliveriesQuery.isLoading && (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-text-muted">
                          Loading…
                        </td>
                      </tr>
                    )}
                    {(deliveriesQuery.data ?? []).map((delivery) => (
                      <tr key={delivery._id} className="border-b border-border/50 align-top">
                        <td className="whitespace-nowrap px-3 py-2 text-text-muted">
                          {formatDateTime(delivery.createdAt)}
                        </td>
                        <td className="px-3 py-2">
                          {delivery.deliveredAt ? (
                            <Badge tone="success">{delivery.statusCode ?? "OK"}</Badge>
                          ) : (
                            <Badge tone="danger">{delivery.statusCode ?? "failed"}</Badge>
                          )}
                        </td>
                        <td className="tnum px-3 py-2 text-right text-text-muted">
                          {delivery.attempt}
                        </td>
                        <td className="tnum px-3 py-2 text-right text-text-muted">
                          {delivery.latencyMs === null ? "—" : `${delivery.latencyMs}ms`}
                        </td>
                        <td className="max-w-xs px-3 py-2">
                          <pre className="scrollbar-slim max-h-24 overflow-auto whitespace-pre-wrap break-all text-xs text-text-muted">
                            {delivery.responseBody ?? "—"}
                          </pre>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!delivery.deliveredAt && (
                            <RowAction onClick={() => replayMutation.mutate(delivery._id)}>
                              Replay
                            </RowAction>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!deliveriesQuery.isLoading && (deliveriesQuery.data ?? []).length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-text-muted">
                          No deliveries yet. Use “Send test” to try the endpoint.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Collapse>
          </Card>
        ))}
      </div>

      {/* Create / edit */}
      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? "Edit endpoint" : "Add endpoint"}
        description="We POST a JSON body and sign it with your endpoint's secret."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={saveMutation.isPending}
              disabled={url.trim() === "" || events.length === 0}
              onClick={() => saveMutation.mutate()}
            >
              {editing ? "Save changes" : "Add endpoint"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="URL"
            placeholder="https://example.com/webhooks/smsfort"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            helperText="Must be publicly reachable — private and link-local addresses are rejected."
            autoFocus
          />

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-text">Events</legend>
            {ALL_EVENTS.map((event) => (
              <label
                key={event.value}
                className="flex cursor-pointer items-start gap-2.5 rounded-control px-2 py-1.5 hover:bg-surface-hover"
              >
                <input
                  type="checkbox"
                  checked={events.includes(event.value)}
                  onChange={() =>
                    setEvents((current) =>
                      current.includes(event.value)
                        ? current.filter((e) => e !== event.value)
                        : [...current, event.value],
                    )
                  }
                  className="mt-0.5 size-4 accent-accent"
                />
                <span className="flex flex-col">
                  <code className="text-sm text-text">{event.value}</code>
                  <span className="text-xs text-text-muted">{event.help}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="wh-headers" className="text-sm font-medium text-text">
              Custom headers (optional)
            </label>
            <textarea
              id="wh-headers"
              rows={3}
              value={headersText}
              onChange={(e) => setHeadersText(e.target.value)}
              placeholder={"Authorization: Bearer abc123\nX-Tenant: acme"}
              className="min-h-[4.25rem] resize-y rounded-control border border-border bg-surface px-3 py-2 font-mono text-xs text-text outline-none transition-colors duration-fast ease-standard placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
            <p className="text-xs text-text-muted">One per line, as “Name: value”.</p>
          </div>

        </div>
      </Modal>

      {/* Secret + verification snippet */}
      <Modal
        open={secretFor !== null}
        onClose={() => {
          setSecretFor(null);
          setSecret(null);
        }}
        title="Signing secret"
        description="Use this to verify the X-SmsBridge-Signature header on every request you receive."
        size="lg"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-control border border-border bg-bg px-3 py-2 text-xs text-text">
              {secret ?? "Loading…"}
            </code>
            <Button
              variant="secondary"
              size="sm"
              disabled={!secret}
              leftIcon={
                copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />
              }
              onClick={() => secret && void copy(secret)}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw className="size-4" aria-hidden />}
              loading={rotateMutation.isPending}
              onClick={() => secretFor && rotateMutation.mutate(secretFor._id)}
            >
              Rotate
            </Button>
          </div>

          <div>
            <p className="mb-1.5 text-sm font-medium text-text">Verify it like this</p>
            <pre className="scrollbar-slim max-h-72 overflow-auto rounded-control border border-border bg-bg p-3 text-xs text-text-muted">
              {VERIFY_SNIPPET}
            </pre>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete this endpoint?"
        description="Its delivery history goes with it. This cannot be undone."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)}
            >
              Delete
            </Button>
          </div>
        }
      />
    </div>
  );
}
